import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Plus, Trash, Lightning } from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';

const emptyRow = () => ({ client_id: '', amount: '', description: '', date: '' });

const BulkSalesEntry = ({ open, onOpenChange, allClientNames, onSuccess }) => {
  const [rows, setRows] = useState([emptyRow(), emptyRow(), emptyRow(), emptyRow(), emptyRow()]);
  const [submitting, setSubmitting] = useState(false);
  const [commonDate, setCommonDate] = useState('');

  const updateRow = (idx, field, value) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const addRows = (count) => {
    setRows(prev => [...prev, ...Array(count).fill(null).map(emptyRow)]);
  };

  const removeRow = (idx) => {
    if (rows.length <= 1) return;
    setRows(prev => prev.filter((_, i) => i !== idx));
  };

  const applyCommonDate = () => {
    if (!commonDate) return;
    setRows(prev => prev.map(r => ({ ...r, date: commonDate })));
    toast.success(`Date applied to all ${rows.length} rows`);
  };

  const clearAll = () => {
    setRows([emptyRow(), emptyRow(), emptyRow(), emptyRow(), emptyRow()]);
    setCommonDate('');
  };

  const validRows = rows.filter(r => r.client_id && r.amount);

  const handleSubmit = async () => {
    if (validRows.length === 0) {
      toast.error('No valid entries. Fill at least client & amount.');
      return;
    }
    setSubmitting(true);
    try {
      const entries = validRows.map(r => ({
        client_id: r.client_id,
        amount: parseFloat(r.amount),
        description: r.description,
        date: r.date ? new Date(r.date).toISOString() : undefined,
      }));
      const { data } = await axios.post(`${BACKEND_URL}/api/sales/bulk`, { entries }, { withCredentials: true });
      if (data.errors?.length > 0) {
        toast.warning(`${data.inserted} recorded, ${data.errors.length} failed`);
      } else {
        toast.success(`${data.inserted} sales recorded — Total: ₹${data.total_amount.toLocaleString('en-IN')}`);
      }
      clearAll();
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit bulk sales');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bulk-sales-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl tracking-tight flex items-center gap-2">
            <Lightning size={22} weight="bold" className="text-primary" />
            Bulk Sales Entry
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-0.5">{validRows.length} of {rows.length} rows ready</p>
        </DialogHeader>

        {/* Common Date Applicator */}
        <div className="flex items-end gap-2 p-3 bg-muted/50 rounded-sm border border-border">
          <div className="flex-1 space-y-1">
            <label className="text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">Apply Date to All Rows</label>
            <Input
              type="date"
              value={commonDate}
              onChange={(e) => setCommonDate(e.target.value)}
              className="rounded-sm h-8 text-sm"
              data-testid="bulk-common-date"
            />
          </div>
          <Button variant="outline" size="sm" onClick={applyCommonDate} disabled={!commonDate} className="rounded-sm h-8 text-xs" data-testid="bulk-apply-date-btn">
            Apply to All
          </Button>
        </div>

        {/* Table Header */}
        <div className="overflow-x-auto">
          <div className="min-w-[620px]">
            <div className="grid grid-cols-[40px_1fr_100px_1fr_120px_36px] gap-1.5 px-1 mb-1">
              <span className="text-[9px] tracking-[0.15em] uppercase font-bold text-muted-foreground">#</span>
              <span className="text-[9px] tracking-[0.15em] uppercase font-bold text-muted-foreground">Client *</span>
              <span className="text-[9px] tracking-[0.15em] uppercase font-bold text-muted-foreground">Amount *</span>
              <span className="text-[9px] tracking-[0.15em] uppercase font-bold text-muted-foreground">Description</span>
              <span className="text-[9px] tracking-[0.15em] uppercase font-bold text-muted-foreground">Date</span>
              <span />
            </div>

            {/* Rows */}
            <div className="space-y-1">
              {rows.map((row, idx) => (
                <div
                  key={idx}
                  className={`grid grid-cols-[40px_1fr_100px_1fr_120px_36px] gap-1.5 items-center px-1 py-0.5 rounded-sm ${row.client_id && row.amount ? 'bg-green-500/5' : ''}`}
                  data-testid={`bulk-row-${idx}`}
                >
                  <span className="text-xs text-muted-foreground font-medium text-center">{idx + 1}</span>
                  <select
                    value={row.client_id}
                    onChange={(e) => updateRow(idx, 'client_id', e.target.value)}
                    className="h-8 px-2 text-xs rounded-sm border border-input bg-background truncate"
                    data-testid={`bulk-client-${idx}`}
                  >
                    <option value="">Select client</option>
                    {allClientNames.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="₹"
                    value={row.amount}
                    onChange={(e) => updateRow(idx, 'amount', e.target.value)}
                    className="rounded-sm h-8 text-xs"
                    data-testid={`bulk-amount-${idx}`}
                  />
                  <Input
                    placeholder="Note"
                    value={row.description}
                    onChange={(e) => updateRow(idx, 'description', e.target.value)}
                    className="rounded-sm h-8 text-xs"
                    data-testid={`bulk-desc-${idx}`}
                  />
                  <Input
                    type="date"
                    value={row.date}
                    onChange={(e) => updateRow(idx, 'date', e.target.value)}
                    className="rounded-sm h-8 text-xs"
                    data-testid={`bulk-date-${idx}`}
                  />
                  <button
                    onClick={() => removeRow(idx)}
                    className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors rounded-sm"
                    title="Remove row"
                    data-testid={`bulk-remove-${idx}`}
                  >
                    <Trash size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => addRows(5)} className="rounded-sm text-xs" data-testid="bulk-add-5-btn">
              <Plus size={12} className="mr-1" /> 5 Rows
            </Button>
            <Button variant="outline" size="sm" onClick={() => addRows(10)} className="rounded-sm text-xs" data-testid="bulk-add-10-btn">
              <Plus size={12} className="mr-1" /> 10 Rows
            </Button>
            <Button variant="ghost" size="sm" onClick={clearAll} className="rounded-sm text-xs text-muted-foreground" data-testid="bulk-clear-btn">
              Clear All
            </Button>
          </div>
          <div className="text-xs text-muted-foreground">
            {validRows.length > 0 && (
              <span>Total: <strong className="text-foreground">₹{validRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0).toLocaleString('en-IN')}</strong></span>
            )}
          </div>
        </div>

        <Button
          onClick={handleSubmit}
          disabled={submitting || validRows.length === 0}
          className="w-full rounded-sm"
          data-testid="bulk-submit-btn"
        >
          {submitting ? 'Recording...' : `Record ${validRows.length} Sale${validRows.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default BulkSalesEntry;
